'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import type { GalleryItem } from '@/lib/cms';

interface GallerySectionProps {
  items: GalleryItem[];
}

export default function GallerySection({ items }: GallerySectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = sectionRef?.current?.querySelectorAll('.reveal');
    if (!els) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('hidden-start');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="gallery" className="py-24 bg-background" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="reveal hidden-start">
            <span className="text-xs font-mono text-muted uppercase tracking-widest block mb-3 border-b border-border w-fit pb-1">
              01 — Portfolio
            </span>
            <h2 className="font-display text-4xl md:text-6xl font-medium text-primary tracking-tight leading-tight">
              A canvas for
              <br />
              <span className="italic text-accent-rose">every vision</span>
            </h2>
          </div>
          <p className="reveal hidden-start reveal-delay-2 text-muted text-base leading-relaxed max-w-sm italic">
            Each design is custom-crafted to reflect your personal aesthetic — no two sets are ever
            the same.
          </p>
        </div>

        {/* Asymmetric Bento Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 auto-rows-[240px] md:auto-rows-[280px]">
          {items?.map((item, i) => (
            <div
              key={item.id}
              className={`gallery-item rounded-2xl overflow-hidden cursor-pointer reveal hidden-start ${item?.span}`}
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <AppImage
                src={item?.src}
                alt={item?.alt}
                fill
                className="object-cover"
                quality={80}
              />
              {/* Hover overlay */}
              <div className="overlay">
                <div className="absolute bottom-0 left-0 p-5 w-full">
                  <span className="text-[10px] font-mono text-white/60 uppercase tracking-widest block mb-1">
                    {item?.style}
                  </span>
                  <h3 className="font-display text-xl font-medium italic text-white">
                    {item?.label}
                  </h3>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA below gallery */}
        <div className="mt-12 text-center reveal hidden-start">
          <a
            href="#booking"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white text-sm font-semibold rounded-full hover:bg-foreground-soft transition-all duration-300 shadow-card"
          >
            Book a Design Like This
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14m-7-7 7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
